import type {
  ChannelLimitData,
  TherapySettingRow,
  TherapySettingsResult as TherapySettingsRenderResult,
  TherapySettingsSection as TherapySettingsRenderSection,
  TherapySettingsSnapshot as TherapySettingsRenderSnapshot,
} from '@lmt/signal-viewer-ui';
import type { TherapyEventDto } from '../api/events-api';
import type {
  TherapySettingParameter,
  TherapySettingsResult,
  TherapySettingsSection,
  TherapySettingsSnapshot,
} from '../api/therapy-archive-api';

export type TherapyArchiveData = {
  /** Null when therapy settings are unavailable or could not be read. */
  therapySettings: TherapySettingsRenderResult | null;
  /** Present only when the backend failed while reading therapy settings. */
  therapySettingsError: string | null;
  /** Alarm-limit segments extracted from the raw therapy-settings payload; empty when settings are unavailable. */
  limits: ChannelLimitData[];
  /** Null when events are unavailable or could not be read; empty when the archive holds none. */
  events: TherapyEventDto[] | null;
  /** Present only when the backend failed while reading events. */
  eventsError: string | null;
};

export type ResolvedSnapshot = {
  startMs: number;
  mode: string;
  parameters: TherapySettingParameter[];
};

export type ResolvedSection = {
  startMs: number;
  endMs: number;
  snapshots: ResolvedSnapshot[];
};

/**
 * Wire sections with timestamps resolved to absolute epoch ms and invalid
 * entries dropped, parameters kept raw. Single source for both the render
 * conversion and limit extraction.
 */
export function resolveSections(
  result: TherapySettingsResult,
): ResolvedSection[] {
  return result.sections
    .map(resolveSection)
    .filter((section): section is ResolvedSection => section != null);
}

function resolveSection(
  section: TherapySettingsSection,
): ResolvedSection | null {
  const startMs = toTimestampMs(section.startTimestampLocal);
  const endMs = toTimestampMs(section.endTimestampLocal);

  if (startMs == null || endMs == null || startMs >= endMs) return null;

  return {
    startMs,
    endMs,
    snapshots: section.snapshots
      .map((snapshot) => resolveSnapshot(snapshot, startMs))
      .filter((snapshot): snapshot is ResolvedSnapshot => snapshot != null),
  };
}

function resolveSnapshot(
  snapshot: TherapySettingsSnapshot,
  sectionStartMs: number,
): ResolvedSnapshot | null {
  const startMs =
    toTimestampMs(snapshot.timestampLocal) ??
    sectionStartMs + snapshot.timeSeconds * 1000;

  if (!Number.isFinite(startMs)) return null;

  return { startMs, mode: snapshot.mode, parameters: snapshot.parameters };
}

export function toTherapySettingsResult(
  sections: ResolvedSection[],
): TherapySettingsRenderResult {
  return { sections: sections.map(toTherapySettingsSection) };
}

function toTherapySettingsSection(
  section: ResolvedSection,
): TherapySettingsRenderSection {
  return {
    startMs: section.startMs,
    endMs: section.endMs,
    snapshots: section.snapshots.map(toTherapySettingsSnapshot),
  };
}

function toTherapySettingsSnapshot(
  snapshot: ResolvedSnapshot,
): TherapySettingsRenderSnapshot {
  return {
    startMs: snapshot.startMs,
    mode: snapshot.mode || null,
    rows: snapshot.parameters
      .filter((parameter) => parameter.isVisible)
      .map(toTherapySettingRow),
  };
}

function toTherapySettingRow(
  parameter: TherapySettingParameter,
): TherapySettingRow {
  const numericValue = Number(parameter.displayValue);
  const id = getTherapySettingRowId(parameter);

  return {
    id,
    label: parameter.name || parameter.localizerKey || id,
    value: parameter.displayValue,
    unit: parameter.unit,
    order: parameter.order,
    numericValue: Number.isFinite(numericValue) ? numericValue : null,
    changeValue: parameter.rawValue,
  };
}

function getTherapySettingRowId(parameter: TherapySettingParameter): string {
  // Negative ids mean the plugin did not expose a real parameter id, so we return another unique key.
  if (parameter.parameterId != null && parameter.parameterId >= 0) {
    return String(parameter.parameterId);
  }

  if (parameter.localizerKey) return parameter.localizerKey;
  if (parameter.name) return parameter.name;
  return `order:${parameter.order}`;
}

function toTimestampMs(value: string | null): number | null {
  if (!value) return null;

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}
